import type { TrfPdfConfigFields, UiOrgAddressConfig } from '@/config/uiConfig'
import type {
  CanonicalGuardianContact,
  CanonicalOrderPayload,
  CanonicalParentInfo,
  Comparator,
} from '@/ordering/canonicalOrder'
import { formatGuardianRelationshipLabel, isOnBehalfSubmit } from '@/ordering/canonicalOrder'
import { getParentComparator } from '@/ordering/comparatorReadAdapter'
import { formatComparatorRelationshipLabel } from '@/ordering/comparatorRelationshipOptions'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import {
  CategorizedHPOOption,
  CollectionType,
  IncidentalFindingsSelection,
  normalizePhenotypicTrait,
  OrderFormInfoData,
  PhenotypicTrait,
  PhysicianInfo,
  RequisitionFormData,
  Sex,
  TestType,
} from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import { getLocalCalendarDateString } from '@/utils/orderFormUtils'
import fontkit from '@pdf-lib/fontkit'
import { PDFDict, PDFDocument, PDFFont, PDFForm, PDFName } from 'pdf-lib'
import states from 'states-us'

const REQUISITION_FORM_TEMPLATE_URL = '/forms/RCIGM_QA_046-RCIGM-CGC-Test-Requisition-Form.pdf'
const REQUISITION_FORM_FONT_URL = '/fonts/NotoSans-Regular.ttf'

/**
 * Total physical family-member sample rows on the TRF (Section J1/J2/J3).
 * Drives how many `familyMemberRows` are rendered when the mother/father slots
 * are absent and general comparators flow into every slot.
 */
const FAMILY_MEMBER_ROW_COUNT = 3

/**
 * Assemble the ordered Section J rows. Fixed parent rows keep their physical
 * positions — mother is always J1 (index 0), father is always J2 (index 1) —
 * so they retain the form's hardcoded sex + relationship. General comparators
 * fill the remaining slots in order (including J1/J2 when the corresponding
 * parent is absent), capped at the number of physical rows. Fixed parent rows
 * keep their physical index, so unfilled slots stay `null` (e.g. father with no
 * general comparators → `[null, father, null]`, keeping father on J2).
 */
function buildFamilyMemberRows(
  mother: PdfParentViewModel | null,
  father: PdfParentViewModel | null,
  generalComparators: readonly PdfParentViewModel[]
): (PdfFamilyMemberRow | null)[] {
  const slots: (PdfFamilyMemberRow | null)[] = new Array(FAMILY_MEMBER_ROW_COUNT).fill(null)
  if (mother) slots[0] = { kind: 'mother', data: mother }
  if (father) slots[1] = { kind: 'father', data: father }

  let next = 0
  for (const g of generalComparators) {
    while (next < slots.length && slots[next] != null) next++
    if (next >= slots.length) break
    slots[next] = { kind: 'general', data: g }
    next++
  }

  return slots
}

/**
 * Acrobat reads the form font from AcroForm `/DR` → `/Font` when the user edits a field (`/DA` points at the font name).
 * pdf-lib only attaches the font to appearance streams (`/AP`), so we add the same font here too.
 */
function registerEmbeddedFontInAcroFormDr(form: PDFForm, font: PDFFont): void {
  // Low-level AcroForm dictionary (tests may omit it on mocks).
  const acroDict = (form as unknown as { acroForm?: { dict: PDFDict } }).acroForm?.dict
  if (!acroDict) return

  // `/DR` = default resource dictionary for the whole form (shared by fields when editing).
  let dr = acroDict.lookupMaybe(PDFName.of('DR'), PDFDict)
  if (!dr) {
    dr = acroDict.context.obj({}) // new empty PDF dict
    acroDict.set(PDFName.of('DR'), dr) // attach under AcroForm
  }

  // `/Font` inside `/DR` maps resource names (e.g. F0) to font objects — same names `/DA` uses.
  let fontResources = dr.lookupMaybe(PDFName.of('Font'), PDFDict)
  if (!fontResources) {
    fontResources = dr.context.obj({})
    dr.set(PDFName.of('Font'), fontResources)
  }

  // Register our embedded font under the name pdf-lib put in default appearance strings.
  fontResources.set(PDFName.of(font.name), font.ref)
}

/** AcroForm text field name for Section H clinical narrative (must match PDF template). */
export const REASON_FOR_TESTING_FIELD_KEY =
  'Reason for Testing Please include patient clinical presentations diagnoses and relevant family history If available include differential diagnosis medical history relevant labsimaging reports specialist notes ie neurology cardiology immunology etc prior genetic testing and family pedigree as attachments Clinical information is crucial for accurate interpretation of results'

/** Research contact: proband uses `E RP EMAIL` / `E RP PHONE`; parents use YES/NO plus channel boxes (RCIGM QA 046). */
export interface PdfResearchContact {
  /** true = consent; false = declined; null = unknown / do not mark parent YES or NO */
  participation: boolean | null
  /** When participation is true, whether to check the Email channel on the TRF */
  preferEmail: boolean
  /** When participation is true, whether to check the Phone channel on the TRF */
  preferPhone: boolean
}

/** Section E guardian / research contact text fields (`CONTACT NAME`, etc.). */
export interface PdfGuardianContactViewModel {
  name: string
  email: string
  phone: string
  relationship: string
}

// ─── PdfViewModel ──────────────────────────────────────────────────────────────

/** Narrow view-model consumed exclusively by populateForm. Neither RequisitionFormData nor
 *  CanonicalOrderPayload escapes into the PDF layer — both are adapted to this shape first. */
export interface PdfParentViewModel {
  firstName: string
  lastName: string
  dateOfBirth: string
  mrn: string
  /**
   * Sex assigned at birth (`'male' | 'female' | 'ambiguous'`; '' when
   * unspecified). Drives the Section J sex checkboxes for general comparator
   * rows. Fixed mother/father rows fall back to their hardcoded FEMALE/MALE
   * when this is empty (legacy forms carry no per-parent sex).
   */
  sex: string
  phenotypicTrait: string
  collectionType: string
  hpoTerms: CategorizedHPOOption[]
  clinicalNote: string
  hasStartedFilling: boolean
  /** When true, TRF uses the incidental-findings opt-in checkbox; when false, the opt-out checkbox. */
  incidentalFindingsOptIn: boolean
  parentEmail: string
  parentPhone: string
  newYorkSampleRetentionOptIn: boolean
  researchContact: PdfResearchContact
  /**
   * Human-readable relationship label (e.g. "Sibling", "Maternal Grandmother").
   * Only populated for the general "additional family member" rows (Section J3+),
   * where the relationship is variable; the fixed mother/father rows hardcode
   * "Biological Mother" / "Biological Father" at the form level.
   */
  relationshipLabel?: string
}

interface PdfPhysicianModel {
  firstName?: string
  lastName?: string
  roleTitle?: string
  /** Post-nominal credentials (e.g. "MD"); falls back to ROLETITLE when no explicit roleTitle. */
  credentials?: string
  phone?: string
  email?: string
}

/**
 * A single family-member sample row as it will be rendered onto the physical
 * TRF (Section J). `kind` selects the row's fixed presentation:
 *  - `'mother'` → hardcoded FEMALE sex + "Biological Mother" relationship.
 *  - `'father'` → hardcoded MALE sex + "Biological Father" relationship.
 *  - `'general'` → variable relationship (`data.relationshipLabel`), no sex box
 *    (comparators do not capture sex).
 *
 * Rows are ordered and assigned to physical slots J1/J2/J3 by array index, so
 * when the fixed mother/father slots are absent the general comparator list
 * flows straight into J1 onward.
 */
export interface PdfFamilyMemberRow {
  kind: 'mother' | 'father' | 'general'
  data: PdfParentViewModel
}

export interface PdfViewModel {
  patient: {
    lastName: string
    firstName: string
    middleName: string
    mrn: string
    dateOfBirth: string
    sex: string
    collectionType: string
    /** Proband specimen id when known (RCIGM row: `SPECIMEN ID`). */
    specimenId: string
    /** Proband Section E incidental findings; AcroForm: `E IF OPT-IN` vs `E IF OPT OUT`. */
    incidentalFindingsOptIn: boolean
    newYorkSampleRetentionOptIn: boolean
    researchContact: PdfResearchContact
    /** Section E contact row; null when no guardian/comparator contact applies. */
    guardianContact: PdfGuardianContactViewModel | null
  }
  institution: {
    name: string
    address: {
      street?: string
      city?: string
      state?: string
      zip?: string
      country?: string
    }
  }
  /** Section C ordering provider + Section G attestation (authorizing clinician when on-behalf). */
  orderingProvider?: PdfPhysicianModel
  /** Logged-in submitter when submitting on behalf of another clinician; Section C additional distribution only. */
  delegate?: PdfPhysicianModel
  patientPhysician?: PdfPhysicianModel
  medicalProfessional: { npi: string; name: string }
  /** Section G attestation date (`YYYY-MM-DD`); falls back to local today when absent (pre-submit preview). */
  attestationDate?: string
  clinical: {
    hpoTerms: CategorizedHPOOption[]
    clinicalNote: string
    differentialDiagnosis: string
    priorGeneticTesting: string
    icd10Diagnosis: string
    familyHistory: string
    hasParent: boolean
    hasGrandparent: boolean
    hasSibling: boolean
    hasCousin: boolean
  }
  /** Test line + CLIA lab name/ID on the TRF (RCIGM QA 046 AcroForm fields). */
  order: { selectedOption: string; cliaLabName: string; cliaLabId: string }
  mother?: PdfParentViewModel | null
  father?: PdfParentViewModel | null
  /**
   * Physical Section J slots (index 0 → J1, 1 → J2, 2 → J3). Fixed parent rows
   * keep their physical index (J1 = mother, J2 = father) so they retain the
   * form's hardcoded sex + relationship; general comparators fill the remaining
   * empty slots in order. `null` marks an unused physical slot. When the fixed
   * mother/father parent comparators are absent, the general comparator list
   * flows straight into the open slots (J1 onward).
   */
  familyMemberRows: (PdfFamilyMemberRow | null)[]
  /**
   * True when the TRF is rendered for an EHR (Epic SMART) launch; false for a standalone web order.
   * Drives EHR-vs-web differences in the rendered form (downstream usage handled by populateForm).
   */
  isEhrOrder: boolean
}

function trimStr(v: string | null | undefined): string {
  return (v ?? '').trim()
}

function physicianToPdfProviderContact(p: PhysicianInfo | undefined): PdfViewModel['orderingProvider'] {
  if (!p) return undefined
  return {
    firstName: p.name?.firstName,
    lastName: p.name?.lastName,
    roleTitle: p.roleTitle,
    credentials: p.credentials,
    phone: p.phone,
    email: p.email,
  }
}

/** Section C contact: authorizing clinician when present, else ordering provider. */
function resolveTrfOrderingProviderContact(info: OrderFormInfoData | undefined): PdfViewModel['orderingProvider'] {
  if (!info) return undefined
  return physicianToPdfProviderContact(info.authorizingProvider ?? info.orderingProvider)
}

/** Section G NPI / printed name: authorizing professional when set, else authorizing provider NPI, else stored medical professional. */
function resolveTrfMedicalProfessional(info: OrderFormInfoData | undefined): { npi: string; name: string } {
  if (!info) return { npi: '', name: '' }

  const ap = info.authorizingProfessionalInfo
  if (ap && (trimStr(ap.npi) || trimStr(ap.name))) {
    return { npi: trimStr(ap.npi), name: trimStr(ap.name) }
  }

  const auth = info.authorizingProvider
  if (auth && trimStr(auth.npi)) {
    const fromNames = [trimStr(auth.name?.firstName), trimStr(auth.name?.lastName)].filter(Boolean).join(' ')
    const name = fromNames || trimStr(info.medicalProfessionalInfo?.name)
    return { npi: trimStr(auth.npi), name }
  }

  return {
    npi: trimStr(info.medicalProfessionalInfo?.npi),
    name: trimStr(info.medicalProfessionalInfo?.name),
  }
}

/**
 * Logged-in submitter for Section C "additional report distribution" when on-behalf only.
 *
 * The delegate is the `orderingProvider` (submitter) but only when it differs from the
 * `authorizingProvider`, matching the API payload via the shared {@link isOnBehalfSubmit}.
 */
function resolveTrfDelegate(info: OrderFormInfoData | undefined): PdfViewModel['delegate'] {
  if (!info || !isOnBehalfSubmit(info.orderingProvider, info.authorizingProvider)) return undefined
  return physicianToPdfProviderContact(info.orderingProvider)
}

/** Join TRF street line 1 and optional line 2 (suite / unit) for a single AcroForm `STREET` field. */
function mergeTrfStreetLines(line1: string, line2: string): string | undefined {
  const a = line1.trim()
  const b = line2.trim()
  if (!a && !b) return undefined
  if (!a) return b
  if (!b) return a
  return `${a}\n${b}`
}

/** TRF Section B: org-config address wins per field when non-blank; else orderFormInfo. */
function mergeInstitutionAddressForTrf(
  info:
    { street?: string; street2?: string; city?: string; state?: string; zip?: string; country?: string } | undefined,
  cfg: UiOrgAddressConfig
): { street?: string; city?: string; state?: string; zip?: string; country?: string } {
  const i = info ?? {}
  const c = cfg ?? {}
  const street1 = trimStr(c.street) || trimStr(i.street)
  const street2 = trimStr(c.street2) || trimStr(i.street2)
  return {
    street: mergeTrfStreetLines(street1, street2),
    city: trimStr(c.city) || trimStr(i.city) || undefined,
    state: trimStr(c.state) || trimStr(i.state) || undefined,
    zip: trimStr(c.zip) || trimStr(i.zip) || undefined,
    country: trimStr(c.country) || trimStr(i.country) || undefined,
  }
}

function probandResearchFromCanonical(canonical: CanonicalOrderPayload): PdfResearchContact {
  const raw = canonical.patient?.researchContactConsent
  if (raw !== true) {
    return { participation: raw === false ? false : null, preferEmail: false, preferPhone: false }
  }
  // Preferred channel follows the resolved guardian contact (a flagged
  // primary-guardian comparator of any relationship, or the standalone slot),
  // falling back to the primary-consent family member.
  const guardian = resolveGuardianContactForPdf(canonical)
  const email =
    trimStr(guardian?.email) ||
    trimStr(canonical.primaryConsent?.familyMember?.email) ||
    trimStr(canonical.guardianContact?.email)
  const phone =
    trimStr(guardian?.phone) ||
    trimStr(canonical.primaryConsent?.familyMember?.phone) ||
    trimStr(canonical.guardianContact?.phone)
  return { participation: true, preferEmail: Boolean(email), preferPhone: Boolean(phone) }
}

/**
 * C1.5: read research-contact participation + preferred channel from a
 * `Comparator`'s nested shape (`secondaryConsent.researchContactConsent`
 * + `demographic.{email,phone}`) instead of the legacy flat slot fields.
 * Same null/false/true semantics as the prior `parentResearchFromCanonical`.
 */
function parentResearchFromComparator(parent: Comparator): PdfResearchContact {
  const raw = parent.secondaryConsent?.researchContactConsent
  if (raw !== true) {
    return { participation: raw === false ? false : null, preferEmail: false, preferPhone: false }
  }
  const email = trimStr(parent.demographic?.email)
  const phone = trimStr(parent.demographic?.phone)
  return { participation: true, preferEmail: Boolean(email), preferPhone: Boolean(phone) }
}

function probandResearchFromRequisition(formData: RequisitionFormData): PdfResearchContact {
  const raw = formData.researchContactConsent
  if (raw !== true) {
    return { participation: raw === false ? false : null, preferEmail: false, preferPhone: false }
  }
  const email = trimStr(formData.primaryConsentFamilyMember?.email) || trimStr(formData.guardianContact?.email)
  const phone = trimStr(formData.primaryConsentFamilyMember?.phone) || trimStr(formData.guardianContact?.phone)
  return { participation: true, preferEmail: Boolean(email), preferPhone: Boolean(phone) }
}

function parentResearchFromRequisition(
  parent: RequisitionFormData['mother'] | RequisitionFormData['father']
): PdfResearchContact {
  if (!parent) {
    return { participation: null, preferEmail: false, preferPhone: false }
  }
  const raw = parent.researchContactConsent
  if (raw !== true) {
    return { participation: raw === false ? false : null, preferEmail: false, preferPhone: false }
  }
  const email = trimStr(parent.parentEmail)
  const phone = trimStr(parent.parentPhone)
  return { participation: true, preferEmail: Boolean(email), preferPhone: Boolean(phone) }
}

function formatParentName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  return [trimStr(firstName), trimStr(lastName)].filter(Boolean).join(' ')
}

function guardianContactToPdf(gc: CanonicalGuardianContact | undefined | null): PdfGuardianContactViewModel | null {
  if (!gc) return null
  const name = formatParentName(gc.firstName, gc.lastName)
  const email = trimStr(gc.email)
  const phone = trimStr(gc.phone)
  const relationship = formatGuardianRelationshipLabel(gc.relationshipToPatient) ?? ''
  if (!name && !email && !phone && !relationship) return null
  return { name, email, phone, relationship }
}

function parentToPdfGuardianContact(
  parent: CanonicalParentInfo,
  relationship: string
): PdfGuardianContactViewModel | null {
  const name = formatParentName(parent.parentFirstName, parent.parentLastName)
  const email = trimStr(parent.parentEmail)
  const phone = trimStr(parent.parentPhone)
  if (!name && !email && !phone) return null
  return { name, email, phone, relationship }
}

function parentHasEmailAndPhone(parent: CanonicalParentInfo): boolean {
  return Boolean(trimStr(parent.parentEmail) && trimStr(parent.parentPhone))
}

/** TRIO: primary guardian first; else father when only he has email+phone; else mother. */
function selectTrioParentForGuardianContact(
  mother: CanonicalParentInfo,
  father: CanonicalParentInfo
): 'mother' | 'father' {
  if (mother.isPrimaryGuardian === true) return 'mother'
  if (father.isPrimaryGuardian === true) return 'father'
  if (parentHasEmailAndPhone(father) && !parentHasEmailAndPhone(mother)) return 'father'
  return 'mother'
}

/** Comparator-model sibling of {@link parentToPdfGuardianContact}. */
function comparatorToPdfGuardianContact(
  comparator: Comparator | null | undefined,
  relationship: string
): PdfGuardianContactViewModel | null {
  if (!comparator) return null
  const demo = comparator.demographic
  const name = formatParentName(demo?.firstName, demo?.lastName)
  const email = trimStr(demo?.email)
  const phone = trimStr(demo?.phone)
  if (!name && !email && !phone) return null
  return { name, email, phone, relationship }
}

function comparatorHasEmailAndPhone(comparator: Comparator): boolean {
  return Boolean(trimStr(comparator.demographic?.email) && trimStr(comparator.demographic?.phone))
}

/**
 * Section E guardian / research contact row. The primary guardian is defined by
 * the `isPrimaryGuardian` flag, not by relationship, so an explicitly flagged
 * comparator (parent or otherwise) wins first, then the standalone guardian
 * contact slot, then — for legacy family orders that never flagged a guardian —
 * the mother/father email+phone heuristic. The relationship label is always the
 * comparator's own captured relationship (no hardcoded parent wording).
 */
function resolveGuardianContactForPdf(canonical: CanonicalOrderPayload): PdfGuardianContactViewModel | null {
  // 1. Explicitly flagged primary-guardian comparator (any relationship).
  const flaggedGuardian = FormCompletionChecker.primaryGuardianComparators(canonical)[0]
  if (flaggedGuardian) {
    const fromFlagged = comparatorToPdfGuardianContact(
      flaggedGuardian,
      formatComparatorRelationshipLabel(flaggedGuardian.relationship)
    )
    if (fromFlagged) return fromFlagged
  }

  // 2. Standalone (non-comparator) guardian contact slot.
  const fromSlot = guardianContactToPdf(canonical.guardianContact)
  if (fromSlot) return fromSlot

  // 3. Legacy fallback: no guardian was flagged, so infer from parent slots.
  //    GEN-3393: parent rows live on `comparators[]` (the `mother` / `father`
  //    slots are inbound-only and stripped at hydration), so read them via the
  //    comparator adapter. The label still comes from each comparator's own
  //    relationship rather than a hardcoded parent string.
  const orderType = canonical.orderType ?? 'PROBAND_ONLY'
  const mother = getParentComparator(canonical, 'mother')
  const father = getParentComparator(canonical, 'father')

  if (orderType === 'FAMILY_DUO_MOTHER' && mother) {
    return comparatorToPdfGuardianContact(mother, formatComparatorRelationshipLabel(mother.relationship))
  }
  if (orderType === 'FAMILY_DUO_FATHER' && father) {
    return comparatorToPdfGuardianContact(father, formatComparatorRelationshipLabel(father.relationship))
  }
  if (orderType === 'FAMILY_TRIO' && mother && father) {
    // Honor an explicitly flagged primary-guardian parent first. Even if that
    // parent has no contact data, we must surface *their* relationship rather
    // than falling through to the email+phone heuristic and labeling the other
    // parent as the guardian. Only when neither parent is flagged do we apply
    // the heuristic (father when only he has email+phone; else mother).
    const flaggedParent = mother.isPrimaryGuardian === true ? mother : father.isPrimaryGuardian === true ? father : null
    if (flaggedParent) {
      const relationship = formatComparatorRelationshipLabel(flaggedParent.relationship)
      return (
        comparatorToPdfGuardianContact(flaggedParent, relationship) ?? {
          name: '',
          email: '',
          phone: '',
          relationship,
        }
      )
    }
    const selected = comparatorHasEmailAndPhone(father) && !comparatorHasEmailAndPhone(mother) ? father : mother
    return comparatorToPdfGuardianContact(selected, formatComparatorRelationshipLabel(selected.relationship))
  }

  return null
}

function resolveGuardianContactForPdfFromLegacy(formData: RequisitionFormData): PdfGuardianContactViewModel | null {
  const hasMother = Boolean(formData.mother)
  const hasFather = Boolean(formData.father)

  if (!hasMother && !hasFather) {
    return guardianContactToPdf(formData.guardianContact)
  }
  if (hasMother && !hasFather && formData.mother) {
    return parentToPdfGuardianContact(formData.mother, 'Biological Mother')
  }
  if (hasFather && !hasMother && formData.father) {
    return parentToPdfGuardianContact(formData.father, 'Biological Father')
  }
  if (hasMother && hasFather && formData.mother && formData.father) {
    const selected = selectTrioParentForGuardianContact(formData.mother, formData.father)
    const parent = selected === 'mother' ? formData.mother : formData.father
    const relationship = selected === 'mother' ? 'Biological Mother' : 'Biological Father'
    return parentToPdfGuardianContact(parent, relationship)
  }
  return null
}

// ─── Adapters ──────────────────────────────────────────────────────────────────

/** @alias TrfPdfConfigFields — use `getTrfPdfConfigFields` from uiConfig at PDF call sites. */
export type TrfPdfFromConfig = TrfPdfConfigFields

/** Convert a CanonicalOrderPayload + OrderFormInfoData into the PDF view-model. */
export function canonicalToPdfViewModel(
  canonical: CanonicalOrderPayload,
  orderFormInfo: OrderFormInfoData,
  trfPdf: TrfPdfFromConfig,
  isEhrOrder = false
): PdfViewModel {
  const p = canonical.patient
  // P10 (MOTHER/FATHER migration): parent rows are read straight off
  // `comparators[]` (the single source of truth) via the comparator read
  // adapter. The PDF view-model still exposes two named slots
  // (`PdfViewModel.mother` / `.father`) because the physical TRF AcroForm has
  // exactly two parent rows (Section J) — that is a presentation detail of
  // the form, not a data-model assumption.
  const m = getParentComparator(canonical, 'mother')
  const f = getParentComparator(canonical, 'father')
  const safeInfo = orderFormInfo ?? ({} as OrderFormInfoData)

  const makeParent = (parent: Comparator | null | undefined): PdfParentViewModel | null => {
    if (!parent) return null
    const demo = parent.demographic
    return {
      firstName: demo?.firstName ?? '',
      lastName: demo?.lastName ?? '',
      dateOfBirth: demo?.dateOfBirth ?? '',
      mrn: demo?.mrn ?? '',
      sex: demo?.sex ?? '',
      phenotypicTrait: normalizePhenotypicTrait(parent.phenotypicTrait),
      collectionType: parent.specimen?.collectionType ?? '',
      hpoTerms: (parent.hpoTerms as unknown as CategorizedHPOOption[]) ?? [],
      clinicalNote: parent.clinicalNote ?? '',
      hasStartedFilling: Boolean(demo?.firstName || demo?.lastName || demo?.dateOfBirth),
      incidentalFindingsOptIn: parentIncidentalFindingsOptInForPdf(parent.secondaryConsent?.includeIncidentalFindings),
      parentEmail: demo?.email ?? '',
      parentPhone: demo?.phone ?? '',
      newYorkSampleRetentionOptIn: parent.secondaryConsent?.newYorkSampleRetentionOptIn === true,
      researchContact: parentResearchFromComparator(parent),
      relationshipLabel: formatComparatorRelationshipLabel(parent.relationship) || undefined,
    }
  }

  // General "additional family member" rows: every comparator that is neither
  // the proband nor a fixed mother/father parent slot (SIBLING, grandparents,
  // aunts/uncles, cousins, etc.). These fill the TRF's general J-rows beyond
  // J1/J2. Capped at the physical row count the form provides.
  const generalComparators: PdfParentViewModel[] = (Array.isArray(canonical.comparators) ? canonical.comparators : [])
    .filter((c): c is Comparator => c != null && c.isPatient !== true && parentSideForComparator(c) === null)
    .map(makeParent)
    .filter((p): p is PdfParentViewModel => p != null && p.hasStartedFilling)

  const motherRow = makeParent(m)
  const fatherRow = makeParent(f)

  // Ordered physical rows (J1/J2/J3). The fixed mother/father slots keep their
  // physical positions (J1 = mother, J2 = father) so they retain the form's
  // hardcoded sex + "Biological Mother/Father" label. Any general comparators
  // then fill the *remaining* slots in order — so when mother/father are absent
  // (being deprecated as separate work) the comparator list flows straight into
  // J1 onward.
  const familyMemberRows = buildFamilyMemberRows(
    motherRow?.hasStartedFilling ? motherRow : null,
    fatherRow?.hasStartedFilling ? fatherRow : null,
    generalComparators
  )

  return {
    patient: {
      lastName: p?.lastName ?? '',
      firstName: p?.firstName ?? '',
      middleName: '',
      mrn: p?.mrn ?? '',
      dateOfBirth: p?.dateOfBirth ?? '',
      sex: p?.sex ?? '',
      collectionType: normalizeCollectionType(p?.collectionType),
      specimenId: trimStr(p?.specimenId),
      incidentalFindingsOptIn: patientIncidentalFindingsOptInForPdf(p?.incidentalFindingsSelection),
      newYorkSampleRetentionOptIn: p?.newYorkSampleRetentionOptIn === true,
      researchContact: probandResearchFromCanonical(canonical),
      guardianContact: resolveGuardianContactForPdf(canonical),
    },
    institution: {
      name: safeInfo.institution?.name ?? '',
      address: mergeInstitutionAddressForTrf(safeInfo.institution?.address, trfPdf.orgAddress),
    },
    orderingProvider: resolveTrfOrderingProviderContact(safeInfo),
    delegate: resolveTrfDelegate(safeInfo),
    patientPhysician: safeInfo.patientPhysician
      ? {
          firstName: safeInfo.patientPhysician.name?.firstName,
          lastName: safeInfo.patientPhysician.name?.lastName,
          roleTitle: safeInfo.patientPhysician.roleTitle,
          credentials: safeInfo.patientPhysician.credentials,
          phone: safeInfo.patientPhysician.phone,
          email: safeInfo.patientPhysician.email,
        }
      : undefined,
    medicalProfessional: resolveTrfMedicalProfessional(safeInfo),
    attestationDate: canonical.primaryConsent?.practitioner?.signatureDate?.trim() || undefined,
    clinical: {
      hpoTerms: (canonical.reason?.clinicalDiagnosis as CategorizedHPOOption[]) ?? [],
      clinicalNote: canonical.clinical?.clinicalNote ?? '',
      differentialDiagnosis: canonical.clinical?.differentialDiagnosis ?? '',
      priorGeneticTesting: canonical.clinical?.priorGeneticTesting ?? '',
      icd10Diagnosis: canonical.reason?.icd10Diagnosis ?? '',
      familyHistory: canonical.family?.familyHistory ?? '',
      hasParent: canonical.family?.hasParent ?? false,
      hasGrandparent: canonical.family?.hasGrandparent ?? false,
      hasSibling: canonical.family?.hasSibling ?? false,
      hasCousin: canonical.family?.hasCousin ?? false,
    },
    order: {
      selectedOption: canonical.order?.selectedOption ?? '',
      // CLIA lab name/ID only apply to DNA collection; suppressed otherwise.
      cliaLabName:
        normalizeCollectionType(p?.collectionType) === CollectionType.DNA ? trimStr(trfPdf.wetLab.labName) : '',
      cliaLabId:
        normalizeCollectionType(p?.collectionType) === CollectionType.DNA ? trimStr(trfPdf.wetLab.cliaLabId) : '',
    },
    mother: makeParent(m),
    father: makeParent(f),
    familyMemberRows,
    isEhrOrder,
  }
}

/** Convert legacy RequisitionFormData + OrderFormInfoData into the PDF view-model. */
function requisitionFormDataToPdfViewModel(
  formData: RequisitionFormData,
  orderFormInfo: OrderFormInfoData,
  trfPdf: TrfPdfFromConfig,
  isEhrOrder = false
): PdfViewModel {
  const makeParent = (
    parent: RequisitionFormData['mother'] | RequisitionFormData['father']
  ): PdfParentViewModel | null => {
    if (!parent) return null
    return {
      firstName: parent.parentFirstName ?? '',
      lastName: parent.parentLastName ?? '',
      dateOfBirth: parent.parentDateOfBirth ?? '',
      mrn: parent.parentMRN ?? '',
      sex: '',
      phenotypicTrait: normalizePhenotypicTrait(parent.phenotypicTrait),
      collectionType: normalizeCollectionType(parent.collectionType),
      hpoTerms: (parent.hpoTerms as unknown as CategorizedHPOOption[]) ?? [],
      clinicalNote: parent.clinicalNote ?? '',
      hasStartedFilling: FormCompletionChecker.hasParentDataStarted(parent),
      incidentalFindingsOptIn: parentIncidentalFindingsOptInForPdf(parent.includeIncidentalFindings),
      parentEmail: parent.parentEmail ?? '',
      parentPhone: parent.parentPhone ?? '',
      newYorkSampleRetentionOptIn: parent.newYorkSampleRetentionOptIn === true,
      researchContact: parentResearchFromRequisition(parent),
    }
  }

  const icd10 =
    typeof formData.reason.icd10Diagnosis === 'string'
      ? formData.reason.icd10Diagnosis
      : Array.isArray(formData.reason.icd10Diagnosis) && formData.reason.icd10Diagnosis.length > 0
        ? (formData.reason.icd10Diagnosis as { id: string }[]).map((c) => c.id).join(', ')
        : ''

  // Legacy RequisitionFormData only carries fixed mother/father slots — no
  // general comparators exist on this path.
  const legacyMotherRow = makeParent(formData.mother)
  const legacyFatherRow = makeParent(formData.father)
  const legacyFamilyMemberRows = buildFamilyMemberRows(
    legacyMotherRow?.hasStartedFilling ? legacyMotherRow : null,
    legacyFatherRow?.hasStartedFilling ? legacyFatherRow : null,
    []
  )

  return {
    patient: {
      lastName: formData.patient.lastName ?? '',
      firstName: formData.patient.firstName ?? '',
      middleName: formData.patient.middleName ?? '',
      mrn: formData.patient.mrn ?? '',
      dateOfBirth: formData.patient.dateOfBirth ?? '',
      sex: formData.patient.sex ?? '',
      collectionType: normalizeCollectionType(formData.patient.collectionType),
      specimenId: trimStr(formData.patient.specimenId),
      incidentalFindingsOptIn: patientIncidentalFindingsOptInForPdf(formData.incidentalFindingsSelection),
      newYorkSampleRetentionOptIn: formData.newYorkSampleRetentionOptIn === true,
      researchContact: probandResearchFromRequisition(formData),
      guardianContact: resolveGuardianContactForPdfFromLegacy(formData),
    },
    institution: {
      name: orderFormInfo?.institution?.name ?? '',
      address: mergeInstitutionAddressForTrf(orderFormInfo?.institution?.address, trfPdf.orgAddress),
    },
    orderingProvider: resolveTrfOrderingProviderContact(orderFormInfo),
    delegate: resolveTrfDelegate(orderFormInfo),
    patientPhysician: orderFormInfo?.patientPhysician
      ? {
          firstName: orderFormInfo.patientPhysician.name?.firstName,
          lastName: orderFormInfo.patientPhysician.name?.lastName,
          roleTitle: orderFormInfo.patientPhysician.roleTitle,
          credentials: orderFormInfo.patientPhysician.credentials,
          phone: orderFormInfo.patientPhysician.phone,
          email: orderFormInfo.patientPhysician.email,
        }
      : undefined,
    medicalProfessional: resolveTrfMedicalProfessional(orderFormInfo),
    attestationDate: formData.consentFormData?.signatureDate?.trim() || undefined,
    clinical: {
      hpoTerms: (formData.reason.clinicalDiagnosis as CategorizedHPOOption[]) ?? [],
      clinicalNote: formData.clinical.clinicalNote ?? '',
      differentialDiagnosis: formData.clinical.differentialDiagnosis ?? '',
      priorGeneticTesting: formData.clinical.priorGeneticTesting ?? '',
      icd10Diagnosis: icd10,
      familyHistory: formData.family.familyHistory ?? '',
      hasParent: formData.family.hasParent ?? false,
      hasGrandparent: formData.family.hasGrandparent ?? false,
      hasSibling: formData.family.hasSibling ?? false,
      hasCousin: formData.family.hasCousin ?? false,
    },
    order: {
      selectedOption: formData.order.selectedOption ?? '',
      // CLIA lab name/ID only apply to DNA collection; suppressed otherwise.
      cliaLabName:
        normalizeCollectionType(formData.patient.collectionType) === CollectionType.DNA
          ? trimStr(trfPdf.wetLab.labName)
          : '',
      cliaLabId:
        normalizeCollectionType(formData.patient.collectionType) === CollectionType.DNA
          ? trimStr(trfPdf.wetLab.cliaLabId)
          : '',
    },
    mother: makeParent(formData.mother),
    father: makeParent(formData.father),
    familyMemberRows: legacyFamilyMemberRows,
    isEhrOrder,
  }
}

// ─── Internal helpers ──────────────────────────────────────────────────────────

/** Maps proband `incidentalFindingsSelection` to TRF Section E (`E IF OPT-IN` vs `E IF OPT OUT`). */
function patientIncidentalFindingsOptInForPdf(selection: IncidentalFindingsSelection | null | undefined): boolean {
  return selection === 'opt-in'
}

/** Maps comparator parent `includeIncidentalFindings` to TRF J1/J2 IF checkboxes. */
function parentIncidentalFindingsOptInForPdf(include: boolean | null | undefined): boolean {
  return include === true
}

/**
 * Normalise a raw ``collectionType`` string to the canonical {@link CollectionType} enum value.
 *
 * IMDB stores specimen types by display name (e.g. `"Buccal"`, `"Blood"`) while the SPA canonical
 * payload uses lowercase enum values (e.g. `"buccal"`, `"whole-blood-edta"`).
 * ``get_order_components`` returns ``specimen_type.name`` verbatim so after submit the API response
 * contains title-cased strings. This normaliser bridges the gap so PDF checkboxes are filled
 * correctly regardless of which path populated the view-model.
 */
function normalizeCollectionType(raw: string | null | undefined): string {
  const s = (raw ?? '').trim().toLowerCase()
  if (s === 'buccal' || s === 'buccal swab') return CollectionType.Buccal
  if (s === 'whole-blood-edta' || s === 'blood') return CollectionType.WholeBloodEDTA
  if (s === 'dna') return CollectionType.DNA
  return raw ?? ''
}

const formatHPOTerms = (hpoTerms: CategorizedHPOOption[] | undefined): string => {
  if (!hpoTerms || hpoTerms.length === 0) return ''
  const grouped = hpoTerms.reduce(
    (acc, term) => {
      const cat = term.category || 'Key'
      ;(acc[cat] = acc[cat] ?? []).push(term)
      return acc
    },
    {} as Record<string, CategorizedHPOOption[]>
  )
  return (['Key', 'Additional', 'Excluded'] as const)
    .filter((c) => grouped[c]?.length)
    .map((c) => {
      const heading = c === 'Excluded' ? 'Pertinent Negative' : c
      return `${heading}: ${grouped[c].map((t) => t.label).join('; ')}`
    })
    .join(' | ')
}

const sectionLabel = (title: string) => `${title}:`

function buildReasonForTestingFieldValue(clinical: PdfViewModel['clinical']): string {
  const parts: string[] = []
  const diagnosis = formatHPOTerms(clinical.hpoTerms).trim()
  if (diagnosis) parts.push(`${sectionLabel('Clinical diagnosis')}\n${diagnosis}`)
  if (clinical.clinicalNote?.trim()) parts.push(`${sectionLabel('Clinical notes')} ${clinical.clinicalNote.trim()}`)
  if (clinical.differentialDiagnosis?.trim()) {
    parts.push(`${sectionLabel('Differential Diagnosis')} ${clinical.differentialDiagnosis.trim()}`)
  }
  if (clinical.priorGeneticTesting?.trim()) {
    parts.push(`${sectionLabel('Prior Genetic Testing')} ${clinical.priorGeneticTesting.trim()}`)
  }

  const relatives = [
    clinical.hasParent ? 'Parent' : '',
    clinical.hasGrandparent ? 'Grandparent' : '',
    clinical.hasSibling ? 'Sibling' : '',
    clinical.hasCousin ? 'Cousin' : '',
  ].filter(Boolean)
  if (relatives.length) parts.push(`Family member(s) with key phenotypes: ${relatives.join(', ')}`)
  if (clinical.familyHistory?.trim()) parts.push(`${sectionLabel('Family history')} ${clinical.familyHistory.trim()}`)

  return parts.join('\n\n')
}

/**
 * Builds the "IF AFFECTED BRIEFLY DESCRIBE PHENOTYPE" value for Section J.
 * Returns an empty string for Unaffected/Unknown comparators so the field
 * remains blank on the TRF. For Affected/PartiallyAffected, combines the
 * free-text clinical summary and structured HPO terms, skipping whichever
 * is absent.
 */
function buildComparatorPhenotypeDescription(parent: PdfParentViewModel | null | undefined): string {
  if (!parent) return ''
  const trait = parent.phenotypicTrait
  if (trait !== PhenotypicTrait.Affected && trait !== PhenotypicTrait.PartiallyAffected) return ''

  const parts: string[] = []
  if (parent.clinicalNote?.trim()) parts.push(parent.clinicalNote.trim())
  const hpo = formatHPOTerms(parent.hpoTerms).trim()
  if (hpo) parts.push(hpo)
  return parts.join('\n')
}

function formatDateForInput(YYYYMMDD: string): string {
  if (!YYYYMMDD) return ''
  const [year, month, day] = YYYYMMDD.split('-').map(Number)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return ''
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`
}

/**
 * AcroForm field names for one physical Section J family-member row. RCIGM QA
 * 046 names the three rows inconsistently (J1/J2 vs J3), so each slot spells
 * out its own field ids. A `null` checkbox name means that option simply has no
 * box on the form for that row and is skipped.
 */
interface TrfFamilyRowFields {
  firstName: string
  lastName: string
  dob: string
  mrn: string
  relationship: string
  phenotype: string
  contactEmail: string
  contactPhone: string
  cliaLabName: string
  cliaLabId: string
  sex: { female: string; male: string; ambiguous: string } | null
  trait: { unaffected: string; affected: string; partiallyAffected: string; unknown: string }
  sample: { wholeBlood: string; buccal: string; dna: string | null }
  incidental: { optIn: string; optOut: string }
  nysOptIn: string
  research: { yes: string; no: string; email: string; phone: string }
}

/** Ordered physical rows J1/J2/J3, indexed to match `vm.familyMemberRows`. */
const TRF_FAMILY_ROW_FIELDS: readonly TrfFamilyRowFields[] = [
  {
    firstName: 'FIRST NAME_3',
    lastName: 'LAST NAME_3',
    dob: 'DOB MMDDYYYY_4',
    mrn: 'MRN',
    relationship: 'RELATIONSHIP TO PATIENT eg mother',
    phenotype: 'Phenotype 1',
    contactEmail: 'CONTACT EMAIL_2',
    contactPhone: 'CONTACT PHONE_2',
    cliaLabName: 'CLIA LAB NAME_2',
    cliaLabId: 'CLIA LAB ID_2',
    sex: { female: 'J1 FEMALE', male: 'J1 MALE', ambiguous: 'J1 AMBIGUOUS' },
    trait: { unaffected: 'J1 CU', affected: 'J1 CA', partiallyAffected: 'J1 PA', unknown: 'J1 UNKNOWN' },
    sample: { wholeBlood: 'J1 WHOLE BLOOD', buccal: 'J1 BUCCAL SWAB', dna: 'J1 EXTRACTED DNA' },
    incidental: { optIn: 'J1 IF OPT-IN', optOut: 'J1 INCIDENTAL FINDINGS OPT-OUT' },
    nysOptIn: 'J1 NYS OPT-IN',
    research: {
      yes: 'J1 RESEARCH PARTICIPATION YES',
      no: 'J1 RESEARCH PARTICIPATION NO',
      email: 'J1 RESEARCH PARTICIPATION EMAIL',
      phone: 'J1 RESEARCH PARTICIPATION PHONE',
    },
  },
  {
    firstName: 'FIRST NAME_4',
    lastName: 'LAST NAME_4',
    dob: 'DOB MMDDYYYY_5',
    mrn: 'MRN_2',
    relationship: 'RELATIONSHIP TO PATIENT eg mother_2',
    phenotype: 'Phenotype 2',
    contactEmail: 'CONTACT EMAIL_3',
    contactPhone: 'CONTACT PHONE_3',
    cliaLabName: 'CLIA LAB NAME_3',
    cliaLabId: 'CLIA LAB ID_3',
    sex: { female: 'J2 FEMALE', male: 'J2 MALE', ambiguous: 'J2 AMBIGUOUS' },
    trait: { unaffected: 'J2 CU', affected: 'J2 CA', partiallyAffected: 'J2 PA', unknown: 'J2 UNKNOWN' },
    sample: { wholeBlood: 'J2 WHOLE BLOOD', buccal: 'J2 BUCCAL SWAB', dna: 'J2 EXTRACTED DNA' },
    incidental: { optIn: 'J2 IF OPT-IN', optOut: 'J2 IF OPT-OUT' },
    nysOptIn: 'J2 NYS OPT-IN',
    research: {
      yes: 'J2 RESEARCH PARTICIPATION YES',
      no: 'J2 RESEARCH PARTICIPATION NO',
      email: 'J2 RESEARCH PARTICIPATION EMAIL',
      phone: 'J2 RESEARCH PARTICIPATION PHONE',
    },
  },
  {
    firstName: 'FIRST NAME_5',
    lastName: 'LAST NAME_5',
    dob: 'DOB MMDDYYYY_6',
    mrn: 'MRN_3',
    relationship: 'RELATIONSHIP TO PATIENT eg mother_3',
    phenotype: 'Phenotype 3',
    contactEmail: 'CONTACT EMAIL_4',
    contactPhone: 'CONTACT PHONE_4',
    cliaLabName: 'CLIA LAB NAME_4',
    cliaLabId: 'J3 CLIA LAB ID',
    sex: { female: 'J3 FEMALE', male: 'J3 MALE', ambiguous: 'J3 AMBIGUOUS' },
    trait: {
      unaffected: 'Clinically Unaffected_3',
      affected: 'Clinically Affected_3',
      partiallyAffected: 'PARTIALLY affected_3',
      unknown: 'J3 AFFECTED UNKNOWN',
    },
    sample: { wholeBlood: 'J3 WB', buccal: 'J3 BUCCAL SWAB', dna: 'J3 EXTRACTED DNA' },
    incidental: { optIn: 'J3 IF OPT-IN', optOut: 'J3 IF OPT-OUT' },
    nysOptIn: 'J3 NYS OPT-IN',
    research: {
      yes: 'J3 RESEARCH PART YES',
      no: 'J3 RESEARCH PART NO',
      email: 'J3 RESEARCH PART EMAIL',
      phone: 'J3 RESEARCH PART PHONE',
    },
  },
]

/** "Biological Mother" / "Biological Father" for fixed parent rows; else the comparator's relationship label. */
function familyRowRelationshipLabel(row: PdfFamilyMemberRow): string {
  if (row.kind === 'mother') return 'Biological Mother'
  if (row.kind === 'father') return 'Biological Father'
  return row.data.relationshipLabel ?? ''
}

/**
 * Populate one physical Section J row (J1/J2/J3) from a family-member row.
 * Fixed mother/father rows check their sex box; general rows leave sex blank
 * and write their relationship label.
 */
function populateFamilyMemberRow(
  form: PDFForm,
  fields: TrfFamilyRowFields,
  row: PdfFamilyMemberRow,
  order: PdfViewModel['order']
): void {
  const data = row.data
  if (!data.hasStartedFilling) return

  if (data.firstName?.trim()) form.getTextField(fields.firstName).setText(data.firstName.trim())
  if (data.lastName?.trim()) form.getTextField(fields.lastName).setText(data.lastName.trim())
  const dob = formatDateForInput(data.dateOfBirth ?? '')
  if (dob) form.getTextField(fields.dob).setText(dob)
  if (data.mrn?.trim()) form.getTextField(fields.mrn).setText(data.mrn.trim())
  const relationship = familyRowRelationshipLabel(row)
  if (relationship) form.getTextField(fields.relationship).setText(relationship)
  const phenotype = buildComparatorPhenotypeDescription(data)
  if (phenotype) form.getTextField(fields.phenotype).setText(phenotype)

  // Sex: prefer the comparator's own sex assigned at birth (comparators are no
  // longer bound to the mother/father binary). Fall back to the fixed
  // mother→FEMALE / father→MALE presentation when a row carries no explicit sex
  // (legacy parent slots). Skipped only when the physical row exposes no sex
  // checkboxes or the value is unspecified.
  if (fields.sex) {
    const sex = data.sex?.trim().toLowerCase()
    if (sex === Sex.Female) form.getCheckBox(fields.sex.female).check()
    else if (sex === Sex.Male) form.getCheckBox(fields.sex.male).check()
    else if (sex === Sex.Ambiguous) form.getCheckBox(fields.sex.ambiguous).check()
    else if (row.kind === 'mother') form.getCheckBox(fields.sex.female).check()
    else if (row.kind === 'father') form.getCheckBox(fields.sex.male).check()
  }

  switch (data.phenotypicTrait) {
    case PhenotypicTrait.Unaffected:
      form.getCheckBox(fields.trait.unaffected).check()
      break
    case PhenotypicTrait.Affected:
      form.getCheckBox(fields.trait.affected).check()
      break
    case PhenotypicTrait.PartiallyAffected:
      form.getCheckBox(fields.trait.partiallyAffected).check()
      break
    default:
      form.getCheckBox(fields.trait.unknown).check()
  }

  switch (data.collectionType) {
    case CollectionType.WholeBloodEDTA:
      form.getCheckBox(fields.sample.wholeBlood).check()
      break
    case CollectionType.Buccal:
      form.getCheckBox(fields.sample.buccal).check()
      break
    case CollectionType.DNA:
      if (fields.sample.dna) form.getCheckBox(fields.sample.dna).check()
      break
  }

  if (data.incidentalFindingsOptIn) {
    form.getCheckBox(fields.incidental.optIn).check()
  } else {
    form.getCheckBox(fields.incidental.optOut).check()
  }

  if (data.newYorkSampleRetentionOptIn) {
    form.getCheckBox(fields.nysOptIn).check()
  }

  const research = data.researchContact
  if (research.participation === true) {
    form.getCheckBox(fields.research.yes).check()
    if (research.preferEmail) form.getCheckBox(fields.research.email).check()
    if (research.preferPhone) form.getCheckBox(fields.research.phone).check()
  } else if (research.participation === false) {
    form.getCheckBox(fields.research.no).check()
  }

  if (data.parentEmail?.trim()) form.getTextField(fields.contactEmail).setText(data.parentEmail.trim())
  if (data.parentPhone?.trim()) form.getTextField(fields.contactPhone).setText(data.parentPhone.trim())

  if (data.collectionType === CollectionType.WholeBloodEDTA) {
    if (order.cliaLabName?.trim()) form.getTextField(fields.cliaLabName).setText(order.cliaLabName.trim())
    if (order.cliaLabId?.trim()) form.getTextField(fields.cliaLabId).setText(order.cliaLabId.trim())
  }
}

// ─── PDF population ────────────────────────────────────────────────────────────

/**
 * GEN-4471: Resolve which TRF Section F checkboxes to check for a given test type.
 * Exported pure function for testability.
 */
export function resolveSectionFCheckboxes(selectedOption: string): string[] {
  const checkboxes: string[] = []

  if (
    selectedOption === TestType.urWGS_Patient_Only ||
    selectedOption === TestType.urWGS_Duo ||
    selectedOption === TestType.urWGS_Trio ||
    selectedOption === TestType.urWGS_Quad
  ) {
    checkboxes.push('F11-03')
  } else if (selectedOption === TestType.rWGS_Patient_Only) {
    checkboxes.push('F10-01')
  } else if (selectedOption === TestType.rWGS_Duo) {
    checkboxes.push('F10-02')
  } else if (selectedOption === TestType.rWGS_Trio) {
    checkboxes.push('F10-03')
  } else if (selectedOption === TestType.rWGS_Quad) {
    // GEN-4471: Rapid quad checks F10-03 (Trio) AND F10-05 (additional family member)
    checkboxes.push('F10-03')
    checkboxes.push('F10-05')
  }

  return checkboxes
}

function populateForm(form: PDFForm, vm: PdfViewModel) {
  form.getTextField(REASON_FOR_TESTING_FIELD_KEY).enableMultiline()

  const orgAddress = vm.institution.address
  if (orgAddress.street?.includes('\n')) {
    form.getTextField('STREET').enableMultiline()
  }
  const assumeUSA =
    orgAddress.country === 'USA' ||
    orgAddress.country === 'United States' ||
    orgAddress.country === 'US' ||
    orgAddress.country === 'United States of America' ||
    (!orgAddress.country &&
      (states.some((s) => s.abbreviation.toLowerCase() === orgAddress.state?.toLowerCase()) ||
        states.some((s) => s.name.toLowerCase() === orgAddress.state?.toLowerCase())))
  const orgState = assumeUSA
    ? (
        states.find((s) => s.abbreviation.toLowerCase() === orgAddress.state?.toLowerCase()) ||
        states.find((s) => s.name.toLowerCase() === orgAddress.state?.toLowerCase())
      )?.abbreviation
    : orgAddress.state

  // Section C ordering provider, Section D additional contact, and the optional Section C
  // "Additional Report Distribution" email differ between EHR (Epic SMART) and web orders.
  let sectionCPhysician: PdfPhysicianModel | undefined
  let sectionDPhysician: PdfPhysicianModel | undefined
  let sectionCAdditionalDistributionEmail: string | undefined

  if (vm.isEhrOrder) {
    // EHR: Section C shows the delegate when on-behalf; Section D mirrors the ordering provider
    // (checkbox) for self-submit, else names the patient physician as the additional contact.
    sectionCPhysician = vm.delegate ? vm.delegate : vm.orderingProvider
    if (!vm.delegate) {
      form.getCheckBox('Same as Ordering Provider').check()
    } else {
      sectionDPhysician = vm.patientPhysician
    }
  } else {
    // Web: Section C is always the ordering/authorizing clinician (the provider the delegate is
    // acting for); Section D is always "Same as Ordering Provider"; the delegate (when present)
    // is surfaced via the Section C "Additional Report Distribution" email only.
    sectionCPhysician = vm.orderingProvider
    form.getCheckBox('Same as Ordering Provider').check()
    sectionCAdditionalDistributionEmail = trimStr(vm.delegate?.email) || undefined
  }

  const textFields: Record<string, string | undefined> = {
    // Section A
    'LAST NAME': vm.patient.lastName,
    'FIRST NAME': vm.patient.firstName,
    'MIDDLE NAME': vm.patient.middleName,
    'PATIENT IDMRN': vm.patient.mrn,
    'DOB MMDDYYYY': formatDateForInput(vm.patient.dateOfBirth),
    // Section B
    INSTITUTION: vm.institution.name,
    STREET: orgAddress.street,
    CITY: orgAddress.city,
    STATE: orgState,
    'B. ZIP CODE': orgAddress.zip,
    'B. STATE': orgState,
    'B. COUNTRY': assumeUSA ? 'USA' : orgAddress.country,
    // Section C
    'C. OP First Name': sectionCPhysician?.firstName,
    'C. OP Last Name': sectionCPhysician?.lastName,
    ROLETITLE: trimStr(sectionCPhysician?.roleTitle) || trimStr(sectionCPhysician?.credentials) || undefined,
    PHONE: sectionCPhysician?.phone,
    'EMAIL institutional email required': sectionCPhysician?.email,
    // Section C — Additional Report Distribution (web: delegate email when submitting on-behalf).
    'C. OP Additional Report Distribution (optional0': sectionCAdditionalDistributionEmail,
    // Section D
    'FIRST NAME_2': sectionDPhysician?.firstName,
    'LAST NAME_2': sectionDPhysician?.lastName,
    ROLETITLE_2: trimStr(sectionDPhysician?.roleTitle) || trimStr(sectionDPhysician?.credentials) || undefined,
    PHONE_2: sectionDPhysician?.phone,
    'EMAIL institutional email required_2': sectionDPhysician?.email,
    // Section G
    'MEDICAL PROFESSIONAL NPI printed': vm.medicalProfessional.npi,
    'MEDICAL PROFESSIONAL NAME printed': vm.medicalProfessional.name,
    'DATE MMDDYYYY': formatDateForInput(vm.attestationDate || getLocalCalendarDateString()),
    // Section H
    [REASON_FOR_TESTING_FIELD_KEY]: buildReasonForTestingFieldValue(vm.clinical),
    'ICD-10 Codes': vm.clinical.icd10Diagnosis,
  }

  const unboundedFields = ['Phenotype 1', 'Phenotype 2', 'Phenotype 3']
  for (const fieldName of unboundedFields) {
    const field = form.getTextField(fieldName)
    field.removeMaxLength()
    field.enableMultiline()
    field.enableScrolling()
    field.setFontSize(5)
  }

  for (const [key, value] of Object.entries(textFields)) {
    if (value?.trim()) form.getTextField(key).setText(value)
  }

  form.getTextField('SPECIMEN ID').setText(vm.patient.specimenId?.trim() ?? '')

  // Specimen + CLIA (proband row; RCIGM QA 046). CLIA lab name/id identify the
  // CLIA lab that isolated the DNA, so only populate them when the proband's
  // specimen is extracted DNA, not buccal/saliva or whole blood.
  if (vm.patient.collectionType === CollectionType.DNA) {
    if (vm.order.cliaLabName?.trim()) {
      form.getTextField('CLIA LAB NAME').setText(vm.order.cliaLabName.trim())
    }
    if (vm.order.cliaLabId?.trim()) {
      form.getTextField('CLIA LAB ID').setText(vm.order.cliaLabId.trim())
    }
  }

  // Section A: sex
  switch (vm.patient.sex) {
    case Sex.Female:
      form.getCheckBox('Check Box2').check()
      break
    case Sex.Male:
      form.getCheckBox('Check Box1').check()
      break
    case Sex.Ambiguous:
      form.getCheckBox('Check Box3').check()
      break
  }

  // Section E: patient sample type
  switch (vm.patient.collectionType) {
    case CollectionType.WholeBloodEDTA:
      form.getCheckBox('E Whole Blood').check()
      break
    case CollectionType.Buccal:
      form.getCheckBox('E Buccal Swab').check()
      break
    case CollectionType.DNA:
      form.getCheckBox('E Extracted DNA').check()
      break
  }
  // Section E: proband incidental findings
  if (vm.patient.incidentalFindingsOptIn) {
    form.getCheckBox('E IF OPT-IN').check()
  } else {
    form.getCheckBox('E IF OPT OUT').check()
  }

  if (vm.patient.newYorkSampleRetentionOptIn) {
    form.getCheckBox('E NYS OPT-IN').check()
  }

  const pResearch = vm.patient.researchContact
  if (pResearch.participation === true) {
    if (pResearch.preferEmail) form.getCheckBox('E RP EMAIL').check()
    if (pResearch.preferPhone) form.getCheckBox('E RP PHONE').check()
    const gc = vm.patient.guardianContact
    if (gc) {
      if (gc.name.trim()) form.getTextField('CONTACT NAME').setText(gc.name.trim())
      if (gc.email.trim()) form.getTextField('CONTACT EMAIL').setText(gc.email.trim())
      if (gc.phone.trim()) form.getTextField('CONTACT PHONE').setText(gc.phone.trim())
      if (gc.relationship.trim()) form.getTextField('RELATIONSHIP TO PATIENT').setText(gc.relationship.trim())
    }
  }

  // Section F: test type
  const checkboxes = resolveSectionFCheckboxes(vm.order.selectedOption)
  for (const name of checkboxes) {
    form.getCheckBox(name).check()
  }

  // Section J (J1/J2/J3): family-member sample rows. Fixed mother/father slots
  // keep their physical index; general comparators fill the remaining empty
  // slots (so when mother/father are absent, comparators fill J1 onward).
  vm.familyMemberRows.slice(0, TRF_FAMILY_ROW_FIELDS.length).forEach((row, index) => {
    if (row) populateFamilyMemberRow(form, TRF_FAMILY_ROW_FIELDS[index], row, vm.order)
  })
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Loads the TRF template, embeds Noto Sans for Unicode-safe field text, populates the form,
 * and returns PDF bytes. Both public render functions go through here so font embedding is
 * always applied regardless of which adapter (legacy or canonical) is used.
 */
async function renderFromViewModel(vm: PdfViewModel): Promise<Uint8Array> {
  const [templateRes, fontRes] = await Promise.all([
    fetch(REQUISITION_FORM_TEMPLATE_URL),
    fetch(REQUISITION_FORM_FONT_URL),
  ])
  if (!templateRes.ok) throw new Error('Failed to fetch PDF template')
  if (!fontRes.ok) throw new Error(`Failed to fetch PDF form font (${fontRes.status})`)

  const [formPdfBytes, fontBytes] = await Promise.all([templateRes.arrayBuffer(), fontRes.arrayBuffer()])

  const pdfDoc = await PDFDocument.load(formPdfBytes)
  // fontkit is required to embed TTF/OTF in the browser bundle.
  pdfDoc.registerFontkit(fontkit)
  // subset: false keeps the full font so Acrobat can use it when editing fields.
  const formFont = await pdfDoc.embedFont(fontBytes, { subset: false })

  const form = pdfDoc.getForm()
  populateForm(form, vm)
  // Critical: pass formFont — default updateFieldAppearances() uses Helvetica (WinAnsi only).
  form.updateFieldAppearances(formFont)
  registerEmbeddedFontInAcroFormDr(form, formFont)

  return pdfDoc.save()
}

/** Render the TRF PDF from legacy RequisitionFormData + OrderFormInfoData. */
export const renderTestRequisitionPDF = async (
  formData: RequisitionFormData,
  orderFormInfo: OrderFormInfoData,
  trfPdf: TrfPdfFromConfig,
  isEhrOrder = false
): Promise<Uint8Array> => {
  try {
    return await renderFromViewModel(requisitionFormDataToPdfViewModel(formData, orderFormInfo, trfPdf, isEhrOrder))
  } catch (error) {
    console.error('Error rendering PDF:', error)
    throw error
  }
}

/** Render the TRF PDF directly from a CanonicalOrderPayload + OrderFormInfoData. */
export const renderTestRequisitionPDFFromCanonical = (
  canonical: CanonicalOrderPayload,
  orderFormInfo: OrderFormInfoData,
  trfPdf: TrfPdfFromConfig,
  isEhrOrder = false
): Promise<Uint8Array> => {
  return renderFromViewModel(canonicalToPdfViewModel(canonical, orderFormInfo, trfPdf, isEhrOrder))
}
